import React, { useEffect, useState } from 'react';
/**
 * `RenewalsVehicleDetails` captures and validates vehicle details for the
 * renewals journey.
 *
 * Behaviour:
 * - Optionally looks up vehicle details by VRN via `VehicleApi`.
 * - Coordinates `VehiclePage` update/validate cycles and enables the "Get
 *   quotes" button only when a vehicle has been loaded/validated.
 * - Handles call-centre vs standard renewals UI differences (header, edit
 *   behaviour, and dealer reference visibility).
 * - Navigates forward to the quote page or back to personal details/landing.
 */
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath,
  VehicleSource
} from '../../../consts/Common';
import {
  ValuesType,
  VehicleState,
  VehicleType
} from '../../common/CommonTypes';
import VehiclePage from '../../common/VehiclePage/VehiclePage';
import { VehicleApi } from '../../../api/VehicleApi';
import PageHeader from '../../common/PageHeader';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { useJourney } from '../Layout';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../../common/HelperMethods';
import { blankVehicle } from '../../testData/journeyHelper';
import { isMandatoryDealerReference } from '../../common/HelperMethods';
import { useAppSelector } from '../../../app-state/Hooks';

const RenewalsVehicleDetails = (): JSX.Element => {
  const {
    onPageChange,
    onUpdateVehicleDetails,
    journey,
    onPolicyStartDateChange
  } = useJourney();
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const { vehicle, policyStartDate } = journey;
  const navigate = useNavigate();
  const [updateVehicleDetails, setUpdateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [validateVehicleDetails, setValidateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [goForward, setGoForward] = useState(false);
  const [goBack, setGoBack] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [vin, setVin] = useState('');

  const { t } = useTranslation();

  const loadingVehicleMessage = t('vehicle-details.loading-vehicle-message');
  const purchasePriceLabel = t('vehicle-details.purchase-price-label');
  const backButtonText = t('vehicle-details.back-button-text');
  const buttonGetQuotesText = t('vehicle-details.button-get-quotes-text');
  const callCentreVehicleDetailsTitle = t(
    'vehicle-details.call-centre-vehicle-details-title'
  );

  const appConfig = useAppSelector((s) => s.appConfig);
  const userToken = useAppSelector((s) => s.user.token);
  const cms = useAppSelector((s) => s.content.vehiclePage);

  const isCallCentre = appConfig.journeyType === Journey.CallCentre;

  const isVehicleManuallySetViaCallCentre =
    isCallCentre && vehicle.vehicleSource === VehicleSource.User;

  const hideDealerReference = isCallCentre
    ? cms.hideDealerReferenceCCI
    : cms.hideDealerReference;

  const mandatoryDealerReference = isMandatoryDealerReference(
    !hideDealerReference,
    isCallCentre,
    cms.mandatoryExternalReferenceCCI,
    cms.mandatoryExternalReference
  );

  const onBack = () => {
    setUpdateVehicleDetails(true);
    setGoBack(true);
  };

  const onNext = () => {
    setValidateVehicleDetails(true);
    setGoForward(true);
  };

  const updateVehicleDetail = (
    key: keyof VehicleType,
    value: ValuesType<VehicleType>
  ) => {
    if (value) {
      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: {
          ...vehicle.vehicleDetails,
          [key]: value
        }
      });
    }
  };

  const initialVehicleDetails: VehicleType = blankVehicle;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (updateVehicleDetails === false && goForward === true) {
      navigate(PageRelativePath.Quote);
      onPageChange(PageAbsolutePath.Renewals.VehicleDetails, true);
    }

    if (updateVehicleDetails === false && goBack === true) {
      if (isCallCentre) {
        const path = setCountryCode(
          PageAbsolutePath.Renewals.Landing,
          appConfig.countryCode
        );
        navigate(path);
      } else {
        navigate(PageRelativePath.PersonalDetails);
        onPageChange(PageAbsolutePath.Renewals.PersonalDetails, false);
      }
    }
  }, [updateVehicleDetails]);

  useEffect(() => {
    if (isVehicleManuallySetViaCallCentre && registrationNumber) {
      return updateVehicleDetail('vrn', registrationNumber);
    }

    if (CommonValidation.isNotEmpty(registrationNumber)) {
      setLoadingVehicle(true);

      const controller = new AbortController();

      fetchVehicle(registrationNumber, controller.signal);

      return () => {
        controller.abort();
      };
    }
  }, [registrationNumber]);

  useEffect(() => {
    if (CommonValidation.isNotEmpty(vin)) {
      return updateVehicleDetail('vin', vin);
    }
  }, [vin]);

  const onUpdateVehicleState = (vs: VehicleState) => {
    onUpdateVehicleDetails(vs);
    setUpdateVehicleDetails(false);
  };

  const onVehicleValidated = (isValid: boolean) => {
    if (isValid === true) {
      setValidateVehicleDetails(false);
      setUpdateVehicleDetails(true);
    } else {
      setValidateVehicleDetails(false);
      setGoForward(false);
    }
  };

  const vehicleNotFound =
    loadingVehicle ||
    vehicle.vehicleDetails.registrationDate === '' ||
    vehicle.vehicleDetails.registrationDate === null;

  async function fetchVehicle(reg: string, signal: AbortSignal) {
    const fetchVehicleSuccess = (userVehicle: VehicleType) => {
      if (
        userVehicle.registrationDate === '' ||
        userVehicle.registrationDate === null
      ) {
        userVehicle.errorOrigin = 'vrn';
        userVehicle.errorMessage = t(
          'common-error-msg.api-error-msg.vehicle-vreg-error'
        );
      }

      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: userVehicle
      });

      setLoadingVehicle(false);
    };

    const fetchVehicleFailure = () => {
      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: {
          ...initialVehicleDetails,
          errorOrigin: 'vrn',
          errorMessage: t('common-error-msg.api-error-msg.vehicle-vreg-error')
        }
      });

      setLoadingVehicle(false);
    };

    await new VehicleApi().getRegisteredVehicles(
      reg,
      userToken,
      fetchVehicleSuccess,
      fetchVehicleFailure,
      signal
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('page-title.vehicle-details')}</title>
      </Helmet>

      <div>
        {isCallCentre ? (
          <h3>{callCentreVehicleDetailsTitle}</h3>
        ) : (
          <PageHeader
            title={cms.title}
            description={cms.description}
            information={cms.information}
            warning={cms.warning}
            descriptionClass='p2'
          />
        )}

        <LoadingContainer
          loading={loadingVehicle}
          message={loadingVehicleMessage}>
          <VehiclePage
            initialVehicleInfo={vehicle}
            policyStartDate={policyStartDate}
            update={updateVehicleDetails || false}
            validate={validateVehicleDetails || false}
            showEditVrn={isCallCentre}
            showGapIntroductionDate={false}
            showModelTrim={isCallCentre}
            showPolicyStartDate={true}
            showSalesId={false}
            showVehicleDeliveryDate={false}
            showDealerReference={!hideDealerReference}
            showFieldsOnDataError={
              (isCallCentre && vehicleNotFound) || !vehicleNotFound
            }
            vehiclePurchasePriceLabel={purchasePriceLabel}
            token={userToken}
            isPolicyStartDateEnabled={false}
            showVin={!cms.hideVinInput || isCallCentre}
            showIsDeliveryDateEstimated={false}
            customisation={cms}
            onValidated={onVehicleValidated}
            ignoreVinFromManualDetails={
              isVehicleManuallySetViaCallCentre || cms.mandatoryVin
            }
            onUpdateVehicleState={onUpdateVehicleState}
            onUpdateRegistrationNumber={setRegistrationNumber}
            onUpdateIdentificationNumber={setVin}
            onUpdatePolicyStartDate={onPolicyStartDateChange}
            isReadOnly={false}
            futureDateAllowed={false}
            showNeedHelpTooltipDeliveryDate={true}
            showVehiclePurchasePrice={true}
            showEditVehicle={isCallCentre}
            showMileage={true}
            isPurchasePriceEditable={true}
            isMileageEditable={true}
            isSalesIdMandatory={false}
            culture={appConfig.cultureCode}
            isVehicleDeliveryEditable={true}
            externalReferenceLabelText={cms.externalReferenceLabelText}
            isDealerReferenceMandatory={mandatoryDealerReference}
          />

          <div className='flex-row form-nav'>
            {appConfig.journeyType !== Journey.CallCentre && (
              <ButtonBack
                name='btnBack'
                onClick={onBack}
                text={backButtonText}
              />
            )}
            <ButtonContinue
              name='btn-see-products'
              onClick={onNext}
              text={buttonGetQuotesText}
              disabled={vehicleNotFound}
            />
          </div>
        </LoadingContainer>
      </div>
    </>
  );
};

export default RenewalsVehicleDetails;
